import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import { X, Download, Share2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ConnectionScore from "./ConnectionScore";

/**
 * ScoreCard modal — session / live scorecard for social sharing.
 * Shows just the *outcome*, never the answers. Safe for couples.
 *
 * props:
 *   open, onClose
 *   hostName, guestName
 *   hostScore, guestScore
 *   roundsPlayed
 *   modeLabel            e.g. "Truth or Dare" or "Deep Talk Session"
 *   packEmoji, packName  optional pack badge
 *   spiceLevel           optional (cute/flirty/spicy)
 *   headline             optional override (else auto-generated)
 */
export default function ScoreCard({
  open,
  onClose,
  hostName,
  guestName,
  hostScore = 0,
  guestScore = 0,
  roundsPlayed = 0,
  modeLabel = "VibeCheck session",
  packEmoji,
  packName,
  spiceLevel,
  headline,
}) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const total = roundsPlayed > 0 ? roundsPlayed : hostScore + guestScore;
  const matched = Math.min(hostScore, guestScore);
  const matchPct = total > 0 ? Math.round((matched * 2 * 100) / (total * 2)) : 0;

  const autoHeadline = total === 0
    ? "Just getting started 🔥"
    : hostScore === guestScore
      ? "We're perfectly in sync 💚"
      : Math.abs(hostScore - guestScore) <= 1
        ? "So close — neck and neck 👀"
        : "Someone's ahead 🏆";

  const toBlob = async () => {
    if (!cardRef.current) return null;
    const dataUrl = await toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#0F0F14",
    });
    const res = await fetch(dataUrl);
    return { blob: await res.blob(), dataUrl };
  };

  const download = async () => {
    setBusy(true);
    try {
      const out = await toBlob();
      if (!out) return;
      const link = document.createElement("a");
      link.href = out.dataUrl;
      link.download = `vibecheck-score-${Date.now()}.png`;
      link.click();
      toast.success("Saved to your device");
    } catch (e) {
      toast.error("Couldn't save. Try share instead.");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const out = await toBlob();
      if (!out) return;
      const file = new File([out.blob], "vibecheck-score.png", { type: "image/png" });
      const text = `${hostName} ❤ ${guestName} · ${hostScore}-${guestScore} on VibeCheck. Think you know your partner better? 😈 vibecheck.app`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "VibeCheck score", text });
      } else {
        download();
      }
    } catch (e) {
      // silent
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm"
          >
            <button
              onClick={onClose}
              className="absolute -top-3 -right-3 z-20 w-9 h-9 rounded-full bg-[#1A1A24] border border-white/10 text-white flex items-center justify-center hover:bg-[#252535] transition"
              data-testid="scorecard-close-button"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* THE CARD */}
            <div
              ref={cardRef}
              className="rounded-[28px] p-8 relative overflow-hidden"
              style={{
                background: "linear-gradient(160deg, #0F0F14 0%, #1A1533 40%, #0F0F14 100%)",
                border: "1px solid rgba(255, 77, 141, 0.3)",
                boxShadow: "0 0 60px rgba(255, 77, 141, 0.25), 0 0 120px rgba(123, 97, 255, 0.15)",
              }}
            >
              {/* Glow blobs */}
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-45 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, #FF4D8D 0%, transparent 70%)" }} />
              <div className="absolute -bottom-24 -left-20 w-56 h-56 rounded-full opacity-40 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 70%)" }} />

              {/* Header */}
              <div className="relative flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#FF4D8D] to-[#7B61FF] flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-lg font-bold text-white tracking-tight">
                    Vibe<span style={{ background: "linear-gradient(135deg, #FF4D8D 0%, #7B61FF 60%, #00E5FF 120%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Check</span>
                  </span>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C9C7D9]">
                  Scorecard
                </span>
              </div>

              {/* Players names */}
              <div className="relative text-center mb-2">
                <p className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">
                  {hostName} <span className="text-[#FF4D8D]">❤</span> {guestName}
                </p>
              </div>

              {/* Headline */}
              <p className="relative text-center text-sm text-[#C9C7D9] mb-7">
                {headline || autoHeadline}
              </p>

              {/* Score big */}
              <div className="relative flex items-center justify-center gap-6 mb-8">
                <ScorePillar name={hostName} score={hostScore} color="#FF4D8D" />
                <span className="text-2xl font-bold text-[#C9C7D9]">vs</span>
                <ScorePillar name={guestName} score={guestScore} color="#00E5FF" />
              </div>

              {/* Stats row */}
              <div className="relative grid grid-cols-3 gap-2 mb-5 text-center">
                <Stat label="Rounds" value={total} />
                <Stat label="Match" value={`${matchPct}%`} />
                <Stat label="Mode" value={modeLabel.split(" ")[0]} />
              </div>

              {/* Connection read — 4 vibe bars */}
              <div className="relative mb-6">
                <ConnectionScore
                  hostScore={hostScore}
                  guestScore={guestScore}
                  roundsPlayed={total}
                  spiceLevel={spiceLevel}
                  modeLabel={modeLabel}
                />
              </div>

              {/* Tags */}
              <div className="relative flex flex-wrap justify-center gap-1.5 mb-6">
                {packEmoji && packName && (
                  <span
                    className="text-[11px] font-bold px-2.5 py-1 rounded-full"
                    style={{ background: "rgba(123, 97, 255, 0.15)", border: "1px solid rgba(123, 97, 255, 0.5)", color: "#FFFFFF" }}
                  >
                    {packEmoji} {packName}
                  </span>
                )}
                {spiceLevel && (
                  <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/5 border border-white/15 text-[#C9C7D9]">
                    {spiceLevel}
                  </span>
                )}
              </div>

              {/* Footer CTA */}
              <div className="relative pt-4 border-t border-white/5 text-center">
                <p className="text-[11px] font-bold text-white mb-0.5">Think you know your partner better?</p>
                <p className="text-[10px] text-[#C9C7D9] font-semibold tracking-wider">vibecheck.app · free couples game</p>
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3 mt-5">
              <button
                onClick={share}
                disabled={busy}
                className="btn-gradient rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60 active:scale-95"
                data-testid="scorecard-share-button"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                Share score
              </button>
              <button
                onClick={download}
                disabled={busy}
                className="bg-white/5 border border-white/10 text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-2 hover:bg-white/10 disabled:opacity-60 active:scale-95 transition"
                data-testid="scorecard-download-button"
              >
                <Download className="w-4 h-4" /> Save
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ScorePillar({ name, score, color }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <div className="flex flex-col items-center">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center font-display-tight text-2xl font-bold text-white mb-2"
        style={{ background: color, boxShadow: `0 0 20px ${color}99` }}
      >
        {initial}
      </div>
      <p className="text-xs font-semibold text-[#C9C7D9] mb-1 truncate max-w-[80px]">{name}</p>
      <p className="font-display-tight text-4xl font-bold" style={{ color }}>{score}</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/5 rounded-xl py-2.5 border border-white/5">
      <p className="text-[10px] font-bold text-[#C9C7D9] uppercase tracking-wider">{label}</p>
      <p className="text-base font-bold text-white capitalize">{value}</p>
    </div>
  );
}
